<template>
  <div>
    <!-- Floating badge: top-left, dismissible -->
    <div
      v-if="isAdmin && !badgeDismissed && showBadge"
      class="apc-float"
      title="Business Setup Progress"
      @click="openCenter"
    >
      <svg class="apc-ring-svg" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
        <circle class="apc-ring-bg" cx="20" cy="20" r="16"/>
        <circle
          class="apc-ring-fill"
          cx="20" cy="20" r="16"
          :stroke="color"
          :stroke-dasharray="circumference"
          :stroke-dashoffset="dashOffset"
        />
      </svg>
      <span class="apc-pct" :style="{ color: color }">{{ percentage }}%</span>
      <button class="apc-dismiss" title="Dismiss" @click.stop="dismiss">
        <i class="bi bi-x"></i>
      </button>
    </div>

    <!-- Full-page Growth Center -->
    <transition name="apc-slide">
      <div v-if="isAdmin && centerOpen" class="apc-center" @click.self="centerOpen = false">
        <div class="apc-center-panel">

          <div class="apc-center-head">
            <div>
              <p class="apc-center-eyebrow">Dukakit</p>
              <h2 class="apc-center-title">Business Setup &amp; Growth Center</h2>
            </div>
            <button class="apc-center-close" @click="centerOpen = false">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <!-- Hero progress ring -->
          <div class="apc-hero">
            <div class="apc-hero-ring-wrap">
              <svg class="apc-hero-svg" viewBox="0 0 80 80">
                <circle class="apc-hero-bg" cx="40" cy="40" r="32"/>
                <circle
                  class="apc-hero-fill"
                  cx="40" cy="40" r="32"
                  :stroke="color"
                  :stroke-dasharray="heroCircumference"
                  :stroke-dashoffset="heroDashOffset"
                />
              </svg>
              <div class="apc-hero-inner">
                <span class="apc-hero-pct" :style="{ color: color }">{{ percentage }}%</span>
              </div>
            </div>
            <div class="apc-hero-text">
              <p class="apc-hero-label">
                <strong>{{ completed }} of {{ total }}</strong> milestones complete
              </p>
              <p class="apc-hero-sub" :style="{ color: color }">{{ statusLabel }}</p>
            </div>
          </div>

          <!-- ── Milestones list ──────────────────────────────────── -->
          <p class="apc-section-label">Setup checklist</p>
          <div class="apc-milestones">
            <div
              v-for="m in milestones"
              :key="m.key"
              :class="['apc-milestone', m.done ? 'apc-milestone--done' : '']"
            >
              <div class="apc-milestone-icon">
                <i v-if="m.done" class="bi bi-check-circle-fill" style="color:#28a745"></i>
                <i v-else class="bi bi-circle" style="color:#dee2e6"></i>
              </div>
              <div class="apc-milestone-body">
                <p class="apc-milestone-label" :class="{ 'text-muted': m.done }">{{ m.label }}</p>
                <p v-if="!m.done && m.desc" class="apc-milestone-desc">{{ m.desc }}</p>
              </div>
              <a
                v-if="!m.done && m.route"
                :href="m.route"
                class="apc-milestone-cta"
                @click="centerOpen = false"
              >Set up <i class="bi bi-arrow-right"></i></a>
              <span v-else-if="m.done" class="apc-milestone-done-badge">Done</span>
            </div>
          </div>

          <div v-if="percentage >= 100" class="apc-complete-banner">
            <i class="bi bi-stars mr-2"></i> Your business is fully set up — you're ready to grow!
          </div>

          <!-- ── Smart Suggestions ───────────────────────────────── -->
          <template v-if="visibleSuggestions.length > 0 || suggestionsLoading">
            <div class="apc-section-divider"></div>

            <div class="apc-section-head">
              <div>
                <p class="apc-section-label mb-0">Smart Suggestions</p>
                <p class="apc-section-sub">Opportunities to get more out of Dukakit</p>
              </div>
              <button
                v-if="dismissedSuggestions.length > 0"
                class="apc-reset-dismissed"
                @click="resetDismissed"
              >
                Show all
              </button>
            </div>

            <div v-if="suggestionsLoading" class="apc-suggestions-skeleton">
              <div v-for="n in 2" :key="n" class="apc-suggestion-ghost"></div>
            </div>

            <div v-else class="apc-suggestions">
              <div
                v-for="s in visibleSuggestions"
                :key="s.id"
                class="apc-suggestion"
              >
                <div class="apc-suggestion-icon">
                  <i :class="['bi', s.icon]"></i>
                </div>
                <div class="apc-suggestion-body">
                  <p class="apc-suggestion-title">{{ s.title }}</p>
                  <p class="apc-suggestion-desc">{{ s.desc }}</p>

                  <!-- Assisted setup: inline M-Pesa flow instead of a link-out -->
                  <template v-if="s.id === 'assisted-setup'">
                    <button
                      v-if="!assistedSetupOpen && !assistedPaid"
                      type="button"
                      class="apc-suggestion-cta"
                      @click="assistedSetupOpen = true"
                    >
                      Get started <i class="bi bi-arrow-right"></i>
                    </button>

                    <p v-if="assistedPaid" class="apc-assisted-paid">
                      <i class="bi bi-check-circle-fill mr-1"></i> Requested — our team will reach out shortly.
                    </p>

                    <div v-if="assistedSetupOpen && !assistedPaid" class="apc-assisted-form">
                      <div v-if="assistedPayError" class="apc-assisted-error">{{ assistedPayError }}</div>

                      <template v-if="!assistedStkSent">
                        <div class="apc-assisted-packages mb-2">
                          <label
                            v-for="pkg in assistedPackages"
                            :key="pkg.value"
                            :class="['apc-assisted-pkg', assistedPackage === pkg.value ? 'apc-assisted-pkg--on' : '']"
                          >
                            <input type="radio" :value="pkg.value" v-model="assistedPackage" class="d-none">
                            KES {{ pkg.value.toLocaleString() }} — {{ pkg.label }}
                          </label>
                        </div>
                        <div class="apc-assisted-phone-row">
                          <span class="apc-assisted-cc">+254</span>
                          <input
                            v-model="assistedPhone"
                            type="tel"
                            class="apc-assisted-phone-input"
                            placeholder="712 345 678"
                            inputmode="numeric"
                            maxlength="10"
                            @input="assistedPhone = assistedPhone.replace(/\D/g,'').slice(0,10)"
                          >
                        </div>
                        <button
                          type="button"
                          class="apc-assisted-pay-btn"
                          :disabled="!assistedPackage || !isAssistedPhoneValid || assistedPayLoading"
                          @click="initiateAssistedPay"
                        >
                          <span v-if="assistedPayLoading">Sending prompt…</span>
                          <span v-else>Pay via M-Pesa <i class="bi bi-arrow-right"></i></span>
                        </button>
                      </template>

                      <template v-else>
                        <p class="apc-assisted-waiting">
                          Check your phone (+254 …{{ assistedPhone.slice(-4) }}) and enter your M-Pesa PIN.
                          Waiting… ({{ assistedPollCountdown }}s)
                        </p>
                        <button type="button" class="apc-suggestion-cta" @click="retryAssistedPay">
                          Didn't get it? Try again
                        </button>
                      </template>
                    </div>
                  </template>
                  <a v-else :href="s.route" class="apc-suggestion-cta" @click="centerOpen = false">
                    Get started <i class="bi bi-arrow-right"></i>
                  </a>
                </div>
                <button
                  class="apc-suggestion-dismiss"
                  title="Dismiss"
                  @click="dismissSuggestion(s.id)"
                >
                  <i class="bi bi-x"></i>
                </button>
              </div>
            </div>
          </template>

        </div>
      </div>
    </transition>
  </div>
</template>

<script>
const MILESTONES_CACHE_KEY  = 'apc_milestones_v1';
const DISMISSED_SUGGEST_KEY = 'apc_dismissed_suggestions_v1';
const CACHE_TTL             = 5 * 60 * 1000; // 5 minutes

export default {
  name: 'ActivationProgress',

  data() {
    return {
      // Milestones
      milestones:    [],
      percentage:    0,
      completed:     0,
      total:         11,
      centerOpen:    false,
      badgeDismissed: false,
      loading:       false,

      // Smart Suggestions
      suggestions:         [],
      suggestionsLoading:  false,
      dismissedSuggestions: [],

      // Assisted setup — inline M-Pesa flow
      assistedSetupOpen:    false,
      assistedPaid:         false,
      assistedPackage:      null,
      assistedPhone:        '',
      assistedPayLoading:   false,
      assistedPayError:     '',
      assistedStkSent:      false,
      assistedPollCountdown: 180,
      assistedPollTimer:    null,
      assistedBillingId:    null,
    };
  },

  computed: {
    // Small badge ring (r=16, circumference ≈ 100.53)
    circumference() { return 2 * Math.PI * 16; },
    dashOffset()    { return this.circumference * (1 - this.percentage / 100); },

    // Large hero ring (r=32, circumference ≈ 201.06)
    heroCircumference() { return 2 * Math.PI * 32; },
    heroDashOffset()    { return this.heroCircumference * (1 - this.percentage / 100); },

    color() {
      if (this.percentage >= 90) return '#28a745';
      if (this.percentage >= 60) return '#fd7e14';
      return '#dc3545';
    },

    statusLabel() {
      if (this.percentage >= 100) return 'Fully set up!';
      if (this.percentage >= 90)  return 'Almost there!';
      if (this.percentage >= 60)  return 'Good progress';
      return 'Getting started';
    },

    isAdmin() {
      return this.$store.state.userDetails?.roles?.[0]?.name === 'Admin';
    },

    showBadge() {
      return this.milestones.length > 0 && this.percentage < 100;
    },

    visibleSuggestions() {
      return this.suggestions.filter(s => !this.dismissedSuggestions.includes(s.id));
    },

    assistedPackages() {
      return [
        { value: 3000,  label: 'Basic — Stock setup & onboarding' },
        { value: 5000,  label: 'Standard — Training + 1 month support' },
        { value: 10000, label: 'Premium — Full setup + 3 month support' },
      ];
    },

    isAssistedPhoneValid() {
      const d = (this.assistedPhone || '').replace(/\D/g, '');
      return (d.length === 10 && (d.startsWith('07') || d.startsWith('01'))) ||
             (d.length === 9  && (d.startsWith('7')  || d.startsWith('1')));
    },
  },

  mounted() {
    this.badgeDismissed       = localStorage.getItem('apc_badge_dismissed') === '1';
    this.dismissedSuggestions = JSON.parse(localStorage.getItem(DISMISSED_SUGGEST_KEY) || '[]');
    if (this.isAdmin) this.fetchMilestones();
    this.$root.$on('open-setup-center', this.openCenter);
  },

  beforeDestroy() {
    this.$root.$off('open-setup-center', this.openCenter);
    clearInterval(this.assistedPollTimer);
  },

  methods: {
    async fetchMilestones() {
      try {
        const cached = JSON.parse(localStorage.getItem(MILESTONES_CACHE_KEY) || 'null');
        if (cached && Date.now() - cached.ts < CACHE_TTL) {
          this.applyMilestoneData(cached.data);
          return;
        }
      } catch (_) {}

      try {
        const { data } = await axios.get('/api/activation-milestones');
        localStorage.setItem(MILESTONES_CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
        this.applyMilestoneData(data);
      } catch (_) {}
    },

    applyMilestoneData(data) {
      this.milestones = data.milestones || [];
      this.percentage = data.percentage || 0;
      this.completed  = data.completed  || 0;
      this.total      = data.total      || 8;
    },

    async fetchSuggestions() {
      this.suggestionsLoading = true;
      try {
        const { data } = await axios.get('/api/smart-suggestions');
        this.suggestions = Array.isArray(data) ? data : [];
      } catch (_) {
        this.suggestions = [];
      } finally {
        this.suggestionsLoading = false;
      }
    },

    dismiss() {
      this.badgeDismissed = true;
      localStorage.setItem('apc_badge_dismissed', '1');
    },

    dismissSuggestion(id) {
      this.dismissedSuggestions = [...this.dismissedSuggestions, id];
      localStorage.setItem(DISMISSED_SUGGEST_KEY, JSON.stringify(this.dismissedSuggestions));
    },

    resetDismissed() {
      this.dismissedSuggestions = [];
      localStorage.removeItem(DISMISSED_SUGGEST_KEY);
    },

    openCenter() {
      if (!this.isAdmin) return;
      localStorage.removeItem(MILESTONES_CACHE_KEY);
      this.fetchMilestones();
      this.fetchSuggestions();
      this.centerOpen = true;
    },

    async initiateAssistedPay() {
      this.assistedPayLoading = true;
      this.assistedPayError   = '';
      try {
        const res = await axios.post('/api/pay-onboarding-fee', {
          mpesaPhoneNumber:   this.e164(this.assistedPhone),
          onboardingPackages: this.assistedPackage,
        });
        this.assistedBillingId = res.data.id;
        this.assistedStkSent   = true;
        this.assistedPollCountdown = 180;
        this.startAssistedPoll();
      } catch (err) {
        this.assistedPayError = err.response?.data?.message || 'Could not start payment. Please try again.';
      } finally {
        this.assistedPayLoading = false;
      }
    },

    startAssistedPoll() {
      clearInterval(this.assistedPollTimer);
      this.assistedPollTimer = setInterval(async () => {
        this.assistedPollCountdown--;
        try {
          const res = await axios.get('/api/validate-mpesa-onboarding', {
            params: { bill_id: this.assistedBillingId },
          });
          if (res.data.message === 1) {
            clearInterval(this.assistedPollTimer);
            this.assistedPaid = true;
            this.dismissSuggestion('assisted-setup');
          } else if (res.data.message === 0) {
            clearInterval(this.assistedPollTimer);
            this.assistedPayError = res.data.description || 'Payment was not completed.';
            this.assistedStkSent  = false;
          }
        } catch (_) {}
        if (this.assistedPollCountdown <= 0) {
          clearInterval(this.assistedPollTimer);
          this.assistedStkSent  = false;
          this.assistedPayError = 'Payment timed out. Please try again.';
        }
      }, 5000);
    },

    retryAssistedPay() {
      clearInterval(this.assistedPollTimer);
      this.assistedStkSent  = false;
      this.assistedPayError = '';
    },

    e164(phone) {
      const d = (phone || '').replace(/\D/g, '');
      return d.length === 9 ? `254${d}` : `254${d.slice(-9)}`;
    },
  },
};
</script>

<style scoped>
/* ── Floating badge ──────────────────────────────────────────────── */
.apc-float {
  position: fixed;
  top: 16px;
  left: 16px;
  z-index: 1050;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
}

.apc-ring-svg {
  position: absolute;
  top: 0; left: 0;
  width: 56px;
  height: 56px;
  transform: rotate(-90deg);
  filter: drop-shadow(0 2px 6px rgba(0,0,0,.15));
}

.apc-ring-bg {
  fill: #fff;
  stroke: #e9ecef;
  stroke-width: 3;
}

.apc-ring-fill {
  fill: none;
  stroke-width: 3;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.6s ease, stroke 0.4s ease;
}

.apc-pct {
  position: relative;
  font-size: 0.6rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.02em;
  transition: color 0.4s ease;
}

.apc-dismiss {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #6c757d;
  border: none;
  color: #fff;
  font-size: 0.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  z-index: 1;
  transition: background 0.15s ease;
}
.apc-dismiss:hover { background: #495057; }

/* ── Full-page center ────────────────────────────────────────────── */
.apc-center {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0,0,0,.4);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.apc-center-panel {
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 16px 16px 0 0;
  padding: 1.5rem 1.25rem 2.5rem;
  -webkit-overflow-scrolling: touch;
}

/* slide-up transition */
.apc-slide-enter-active,
.apc-slide-leave-active {
  transition: opacity 0.25s ease, transform 0.3s cubic-bezier(.32,.85,.56,1);
}
.apc-slide-enter      { opacity: 0; }
.apc-slide-leave-to   { opacity: 0; }
.apc-slide-enter .apc-center-panel,
.apc-slide-leave-to .apc-center-panel {
  transform: translateY(100%);
}
.apc-slide-enter-active .apc-center-panel,
.apc-slide-leave-active .apc-center-panel {
  transition: transform 0.3s cubic-bezier(.32,.85,.56,1);
}

/* header */
.apc-center-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}
.apc-center-eyebrow {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #fd4719;
  margin: 0 0 4px;
}
.apc-center-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #212529;
  margin: 0;
  line-height: 1.3;
}
.apc-center-close {
  border: none;
  background: #f8f9fa;
  color: #495057;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 0.9rem;
  transition: background 0.15s ease;
}
.apc-center-close:hover { background: #e9ecef; }

/* hero */
.apc-hero {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  background: #f8f9fa;
  border-radius: 10px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.25rem;
}

.apc-hero-ring-wrap {
  position: relative;
  width: 80px;
  height: 80px;
  flex-shrink: 0;
}

.apc-hero-svg {
  width: 80px;
  height: 80px;
  transform: rotate(-90deg);
}

.apc-hero-bg {
  fill: #fff;
  stroke: #e9ecef;
  stroke-width: 5;
}

.apc-hero-fill {
  fill: none;
  stroke-width: 5;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.6s ease, stroke 0.4s ease;
}

.apc-hero-inner {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.apc-hero-pct {
  font-size: 1.1rem;
  font-weight: 800;
  line-height: 1;
  transition: color 0.4s ease;
}

.apc-hero-label {
  font-size: 0.9rem;
  color: #212529;
  margin: 0 0 4px;
  line-height: 1.3;
}
.apc-hero-sub {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin: 0;
  transition: color 0.4s ease;
}

/* ── Section labels ──────────────────────────────────────────────── */
.apc-section-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #adb5bd;
  margin: 0 0 0.6rem;
}

.apc-section-divider {
  border-top: 1.5px solid #f0f0f0;
  margin: 1.5rem 0 1.25rem;
}

.apc-section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.apc-section-sub {
  font-size: 0.75rem;
  color: #6c757d;
  margin: 2px 0 0;
}

.apc-reset-dismissed {
  border: none;
  background: none;
  font-size: 0.72rem;
  color: #fd4719;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: 0.5rem;
}
.apc-reset-dismissed:hover { opacity: 0.75; }

/* ── Milestones ──────────────────────────────────────────────────── */
.apc-milestones {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 1rem;
}

.apc-milestone {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fff;
  border: 1.5px solid #f0f0f0;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.apc-milestone--done { background: #f8fff9; border-color: #d4edda; }

.apc-milestone-icon { flex-shrink: 0; font-size: 1rem; line-height: 1; }

.apc-milestone-body { flex: 1; min-width: 0; }
.apc-milestone-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: #212529;
  margin: 0;
  line-height: 1.3;
}
.apc-milestone--done .apc-milestone-label { text-decoration: line-through; }

.apc-milestone-desc {
  font-size: 0.72rem;
  color: #6c757d;
  margin: 2px 0 0;
  line-height: 1.35;
}

.apc-milestone-cta {
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #fd4719;
  text-decoration: none;
  white-space: nowrap;
  transition: opacity 0.15s ease;
}
.apc-milestone-cta:hover { opacity: 0.75; text-decoration: none; color: #fd4719; }

.apc-milestone-done-badge {
  flex-shrink: 0;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #28a745;
}

/* complete banner */
.apc-complete-banner {
  background: #d4edda;
  color: #155724;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  text-align: center;
  margin-top: 0.5rem;
}

/* ── Smart Suggestions ───────────────────────────────────────────── */
.apc-suggestions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.apc-suggestion {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fff;
  border: 1.5px solid #e9ecef;
  position: relative;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.apc-suggestion:hover {
  border-color: #dee2e6;
  box-shadow: 0 2px 8px rgba(0,0,0,.06);
}

.apc-suggestion-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #fff3f0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fd4719;
  font-size: 1rem;
}

.apc-suggestion-body {
  flex: 1;
  min-width: 0;
  padding-right: 20px;
}

.apc-suggestion-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #212529;
  margin: 0 0 3px;
  line-height: 1.3;
}

.apc-suggestion-desc {
  font-size: 0.72rem;
  color: #6c757d;
  margin: 0 0 6px;
  line-height: 1.4;
}

.apc-suggestion-cta {
  border: none;
  background: none;
  padding: 0;
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  color: #fd4719;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.15s ease;
}
.apc-suggestion-cta:hover { opacity: 0.75; text-decoration: none; color: #fd4719; }

.apc-suggestion-dismiss {
  position: absolute;
  top: 8px;
  right: 8px;
  border: none;
  background: none;
  color: #adb5bd;
  font-size: 0.75rem;
  cursor: pointer;
  padding: 2px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: color 0.15s ease, background 0.15s ease;
}
.apc-suggestion-dismiss:hover { color: #6c757d; background: #f0f0f0; }

/* ── Assisted setup inline form ───────────────────────────────────── */
.apc-assisted-form  { margin-top: 8px; }
.apc-assisted-error {
  font-size: 0.72rem;
  color: #dc3545;
  margin-bottom: 6px;
}
.apc-assisted-paid {
  font-size: 0.72rem;
  font-weight: 600;
  color: #28a745;
  margin: 0;
}
.apc-assisted-packages { display: flex; flex-direction: column; gap: 6px; }
.apc-assisted-pkg {
  font-size: 0.75rem;
  padding: 8px 10px;
  border: 1.5px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
  margin: 0;
  transition: border-color 0.15s ease;
}
.apc-assisted-pkg--on { border-color: #fd4719; }
.apc-assisted-phone-row {
  display: flex;
  align-items: center;
  border: 1.5px solid #dee2e6;
  border-radius: 6px;
  padding: 6px 10px;
  margin-bottom: 8px;
}
.apc-assisted-cc { font-size: 0.75rem; color: #6c757d; margin-right: 6px; }
.apc-assisted-phone-input {
  border: none;
  outline: none;
  font-size: 0.8rem;
  flex: 1;
  min-width: 0;
}
.apc-assisted-pay-btn {
  width: 100%;
  border: none;
  border-radius: 6px;
  background: #fd4719;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 8px 10px;
  cursor: pointer;
}
.apc-assisted-pay-btn:disabled { opacity: 0.5; cursor: default; }
.apc-assisted-waiting {
  font-size: 0.72rem;
  color: #6c757d;
  margin-bottom: 6px;
}

/* skeleton loader */
.apc-suggestions-skeleton { display: flex; flex-direction: column; gap: 8px; }

.apc-suggestion-ghost {
  height: 72px;
  border-radius: 10px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: apc-shimmer 1.4s infinite;
}

@keyframes apc-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
